/**
 * Step 0 — Resumo da oferta e decisão.
 *
 * Réplica do frame `web-login`. A ordem da tela é a do design: chip da seção,
 * saudação, aviso de prazo, CPF, escolha, LGPD e só então "Próximo".
 *
 * O ponto que mais importa aqui é que manter e dispensar são uma SELEÇÃO, não
 * ações imediatas: o titular escolhe um dos dois botões, marca a LGPD e confirma
 * em "Próximo". Recusar só dispara a chamada depois da confirmação no modal,
 * porque o `decline` consome o token (spec §5) e não tem volta.
 *
 * A conferência do CPF é local, contra o `holder.taxPayerId` do resolve: serve
 * para o titular perceber que abriu o link de outra pessoa, não como barreira de
 * segurança — quem autentica é o token.
 */

import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Alert, Checkbox, TextField } from "@mui/material";
import { useState } from "react";
import styled from "styled-components";

import { isValidCPF, cpfInputMask } from "utils/cpf";

import { declineExtension } from "../api";
import { DeclineConfirmModal } from "../components/DeclineConfirmModal";
import { useExtensionForm } from "../context";
import { formatIsoDate } from "../helpers";
import { Button, Paragraph, Row, Stack } from "../styled";
import { COLORS } from "../tokens";

const CPF_DIGITS = 11;

const PRIVACY_POLICY_URL = "https://www.conducco.ai/privacy-policy";

/** Pill contornada "Resumo da Oferta" do topo do frame. */
const SectionPill = styled.span`
  align-self: flex-start;
  border: solid 1px ${COLORS.border};
  border-radius: 999px;
  padding: 4px 12px;
  color: ${COLORS.inkMuted};
  font-size: 12px;
  line-height: 1.66;
`;

/** Os dois botões de escolha ficam lado a lado, como no design. */
const ChoiceRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export function Step0Login() {
  const { organizationId, token, data, state, updateState, goNext, setOutcome } =
    useExtensionForm();
  const { offer, holder } = data;

  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineError, setDeclineError] = useState<string>();

  const typedCpf = state.taxPayerId;
  const isComplete = typedCpf.length === CPF_DIGITS;
  const isCpfMatching = isComplete && typedCpf === holder.taxPayerId;

  /**
   * O erro só aparece com os 11 dígitos preenchidos, para o campo não ficar
   * vermelho enquanto o titular ainda digita.
   */
  const cpfErrorMessage = (() => {
    if (!isComplete) return undefined;
    if (!isValidCPF(typedCpf)) return "CPF inválido.";
    if (!isCpfMatching) return "Este CPF não corresponde ao convite recebido.";

    return undefined;
  })();

  const canProceed =
    isCpfMatching && state.hasAcceptedLgpd && !!state.offerDecision;

  function handleNext() {
    if (state.offerDecision === "DECLINE") {
      setIsDeclineOpen(true);

      return;
    }

    goNext();
  }

  async function handleConfirmDecline() {
    setIsDeclining(true);
    setDeclineError(undefined);

    try {
      await declineExtension(organizationId, token);
      setOutcome("DECLINED");
    } catch (error) {
      setDeclineError(
        "Não foi possível registrar sua recusa. Tente novamente em alguns instantes."
      );
    } finally {
      setIsDeclining(false);
    }
  }

  return (
    <Stack $gap="24px">
      <SectionPill>Resumo da Oferta</SectionPill>

      <Paragraph>
        Olá, <strong>{offer.beneficiaryName}</strong>. Seu benefício pela{" "}
        <strong>{offer.companyName}</strong> encerrou, mas você tem o direito de
        manter o seu plano de saúde.
      </Paragraph>

      <Alert
        severity="warning"
        icon={<CalendarTodayOutlinedIcon fontSize="small" />}
      >
        Você tem até o dia <strong>{formatIsoDate(offer.expirationDate)}</strong>{" "}
        para decidir caso não escolha uma opção depois da data será considerado
        como recusado.
      </Alert>

      <Paragraph>
        Digite o seu CPF e selecione uma opção se deseja manter ou se quer
        dispensar o benefício.
      </Paragraph>

      <TextField
        label="CPF"
        placeholder="Digite seu CPF"
        value={cpfInputMask(typedCpf)}
        onChange={(event) =>
          updateState({
            taxPayerId: event.target.value
              .replace(/\D/g, "")
              .substring(0, CPF_DIGITS),
          })
        }
        disabled={isDeclining}
        error={!!cpfErrorMessage}
        helperText={
          cpfErrorMessage ??
          "O CPF é necessário para nos ajudar a buscar o seu cadastro no sistema."
        }
      />

      <ChoiceRow>
        <Button
          variant={state.offerDecision === "KEEP" ? "contained" : "outlined"}
          onClick={() => updateState({ offerDecision: "KEEP" })}
          disabled={isDeclining}
        >
          Sim, quero manter
        </Button>
        <Button
          variant={state.offerDecision === "DECLINE" ? "contained" : "outlined"}
          onClick={() => updateState({ offerDecision: "DECLINE" })}
          disabled={isDeclining}
        >
          Não, dispensar
        </Button>
      </ChoiceRow>

      <Row $gap="0">
        <Checkbox
          size="small"
          checked={state.hasAcceptedLgpd}
          disabled={isDeclining}
          onChange={() =>
            updateState({ hasAcceptedLgpd: !state.hasAcceptedLgpd })
          }
        />
        <Paragraph>
          Li e estou de acordo com a{" "}
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noreferrer"
            style={{ color: COLORS.brand }}
          >
            Política de Privacidade
          </a>{" "}
          da plataforma.
        </Paragraph>
      </Row>

      <Button
        variant="contained"
        onClick={handleNext}
        disabled={!canProceed || isDeclining}
        fullWidth
      >
        Próximo
      </Button>

      <DeclineConfirmModal
        open={isDeclineOpen}
        isBusy={isDeclining}
        errorMessage={declineError}
        onConfirm={handleConfirmDecline}
        onCancel={() => {
          setIsDeclineOpen(false);
          setDeclineError(undefined);
        }}
      />
    </Stack>
  );
}
